import { useNavigate } from "react-router-dom";
import {
  Accordion,
  Avatar,
  Button,
  Flex,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { Instruments, TagCapsules, TagStatus } from "../../../../components";
import { IColor } from "../../../../utils/selectColors";
import { EPlanStatus, IMyPlans } from "../../../../interfaces/MyPlans/IMyPlans";
import AccordionInfo from "./AccordionInfo";
import { TEMP_GraficProduct } from "../../../../assets";
import {
  tradutorCategory,
  tradutorSubcategory,
} from "../../../../utils/SubCategorys";
import StepsNew from "../Components/StepsNew";

interface IProps {
  product: IMyPlans;
}

export default function CardProduct({ product }: IProps) {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMachineRunning = !!product?.is_machine_running;
  const isAcceptedTerm = !!product?.is_accepted_term;

  const variantColors: Record<EPlanStatus, { color: IColor; text: string }> = {
    All: {
      color: IColor.success,
      text: t("meu_portfolio.minhas_compras.content_tabs.all"),
    },
    Approved: {
      color: IColor.success,
      text: t("meu_portfolio.minhas_compras.content_tabs.approved"),
    },
    InProcessing: {
      color: IColor.loading,
      text: t("meu_portfolio.minhas_compras.content_tabs.in_processing"),
    },
    Refused: {
      color: IColor.disapproved,
      text: t("meu_portfolio.minhas_compras.content_tabs.refused"),
    },
    CancelRequested: {
      color: IColor.orange,
      text: t("meu_portfolio.minhas_compras.content_tabs.cancel_requested"),
    },
    Cancelled: {
      color: IColor.waiting,
      text: t("meu_portfolio.minhas_compras.content_tabs.cancelled"),
    },
  };

  const selectedVariant =
    variantColors[product.status] || variantColors[EPlanStatus.InProcessing];

  const handleRedirectToOrders = () => {
    navigate(`/operacoes/ordens?productId=${product.product.id}`);
  };

  const handleRedirectToPositions = () => {
    navigate(`/operacoes/posicoes?productId=${product.product.id}`);
  };

  const handleRedirectToConfiguration = () => {
    navigate(
      `/meu-portfolio/minhas-compras/configuracao-do-servico/id=${product.id}`
    );
  };

  return (
    <>
      <Flex
        sx={{
          boxShadow: "0px 10px 40px rgba(0, 0, 0, 0.05)",
          borderRadius: "12px",
        }}
      >
        <Flex
          w="100%"
          id="CardProduct"
          p={{ base: 24, 991: 32 }}
          direction={"column"}
        >
          <Flex
            p="12px"
            gap="10px"
            style={{
              borderRadius: "8px",
            }}
            bg={theme.colors.basic[1]}
            w="100%"
            justify={
              product.status === EPlanStatus.Approved ? "space-between" : "end"
            }
            mb="16px"
            align="center"
            direction={"row"}
            wrap={{ base: "wrap", 991: "nowrap" }}
          >
            {product.status === EPlanStatus.Approved ? (
              <StepsNew product={product} />
            ) : null}
            <Button
              variant={"primary"}
              miw={{ base: "100%", 991: "280px" }}
              w={{ base: "100%", 991: "fit-content" }}
              onClick={handleRedirectToConfiguration}
              disabled={
                product?.status !== EPlanStatus.Approved &&
                product?.status !== EPlanStatus.InProcessing &&
                product?.status !== EPlanStatus.CancelRequested
              }
            >
              {t(
                "meu_portfolio.minhas_compras.card_product.button_configuration"
              )}
            </Button>
          </Flex>
          <Flex
            id="lineOneCardProduct"
            justify="space-between"
            align={{ base: "start", 991: "center" }}
            pb={17}
            sx={{ borderBottom: `1px solid ${theme.colors.basic[2]}` }}
            gap={{ base: 16 }}
            direction={{ base: "column-reverse", 991: "row" }}
          >
            <Flex gap={16} align="center" w="100%">
              <Avatar
                src={
                  !!product?.product?.icon
                    ? product?.product?.icon
                    : TEMP_GraficProduct
                }
                size={80}
                radius={8}
              />
              <Flex justify="center" direction="column" w="100%">
                <Flex gap="8px">
                  <TagCapsules colorTag={IColor.orange} border>
                    {t(tradutorCategory(product?.product?.category).title)}
                  </TagCapsules>
                  {product?.product?.subcategory ? (
                    <TagCapsules colorTag={IColor.yellow} border>
                      {t(
                        tradutorSubcategory(product?.product?.subcategory).title
                      )}
                    </TagCapsules>
                  ) : null}
                </Flex>
                <Text
                  w="100%"
                  style={{
                    whiteSpace: "wrap",
                    wordWrap: "break-word",
                  }}
                  fz={{ base: 18 }}
                  fw={{ base: 600 }}
                  lh={{ base: "155%" }}
                  c="basic.5"
                >
                  {product?.product?.title}
                </Text>
              </Flex>
            </Flex>
            <Flex
              direction={{ base: "row", 991: "column" }}
              gap={8}
              align={{ base: "center", 991: "end" }}
              wrap={"wrap"}
            >
              <TagStatus
                status={selectedVariant.color}
                title={selectedVariant.text}
                border={true}
              />
              <Text
                fz={{ base: 16 }}
                lh={{ base: "155%" }}
                c="basic.4"
                style={{
                  whiteSpace: "nowrap",
                }}
              >
                {t("meu_portfolio.minhas_compras.card_product.code_buy", {
                  product_id: product?.id,
                })}
              </Text>
            </Flex>
          </Flex>

          <Accordion w={"100%"}>
            <AccordionInfo product={product} />
          </Accordion>
          <Flex
            gap={{ base: 32 }}
            w="fit"
            justify="space-between"
            align={"end"}
            wrap={{ base: "wrap", 1300: "nowrap" }}
            py={{ base: 16, 991: 22 }}
            sx={{ borderBottom: `1px solid ${theme.colors.basic[2]}` }}
          >
            <Instruments
              instruments={product?.product?.instruments?.symbols}
              idProduct={product?.product?.id}
              isCripto={product?.product?.market === "CRIPTO"}
            />
          </Flex>
          <Flex
            gap={{ base: 16 }}
            w="fit"
            justify="space-between"
            wrap={{ base: "wrap", 991: "nowrap" }}
            pt={{ base: 16, 991: 22 }}
          >
            <Button
              variant={isAcceptedTerm ? "primaryOutline" : "primary"}
              miw="225px"
              w={{ base: "100%" }}
              onClick={handleRedirectToPositions}
              disabled={
                !!!product.is_accepted_term ||
                (product.status !== EPlanStatus.Approved &&
                  product?.status !== EPlanStatus.InProcessing)
              }
            >
              {t(
                "meu_portfolio.minhas_compras.card_product.button_view_positions"
              )}
            </Button>
            <Button
              variant={isMachineRunning ? "primaryOutline" : "primary"}
              miw="225px"
              w={{ base: "100%" }}
              onClick={handleRedirectToOrders}
              disabled={
                !!!product.is_accepted_term ||
                (product.status !== EPlanStatus.Approved &&
                  product?.status !== EPlanStatus.InProcessing)
              }
            >
              {t(
                "meu_portfolio.minhas_compras.card_product.button_view_orders"
              )}
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </>
  );
}
